import React, { useEffect, useRef, useState } from 'react';
import { Toast } from 'react-bootstrap';
import { CheckCircleFill } from 'react-bootstrap-icons';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import type { RootState } from '../../../../../store';
import { SystemPermission } from '../../../../../context';
import AddAssignToCircleButton from './AddAssignToCircleButton';
import { useLoggedInUser } from '../../../../../context/LoggedInUserContext';
import { useAssignPatient } from '../../../../hooks';

type AssignedClinician = {
  id?: number | string;
  clinician_id: number;
  first_name: string;
  last_name: string;
};

interface ActionFormatterComponentProps {
  patientId: number;
  assignedTo?: AssignedClinician[];
  recommended?: boolean;
  onSelectAssignTo: (
    value: string,
    callBack: (
      isAssigned: boolean,
      patientAccessToken: string,
      clinicianId?: number,
    ) => Promise<any>,
    clinicianId: number,
  ) => Promise<any> | void;
  inPatientDetails?: boolean;
  hasClinicianOptions?: boolean;
}

const ActionFormatterComponent = ({
  assignedTo,
  patientId,
  onSelectAssignTo,
  inPatientDetails,
  hasClinicianOptions,
  recommended,
}: ActionFormatterComponentProps) => {
  const { assignToClinician } = useAssignPatient();
  const [assigningToOpen, setAssigningToOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<SVGElement | null>(null);
  const [toastStyle, setToastStyle] = useState<React.CSSProperties>();
  const [localAssignedTo, setLocalAssignedTo] = useState<AssignedClinician[]>(
    Array.isArray(assignedTo) ? assignedTo : [],
  );
  useEffect(() => {
    setLocalAssignedTo(Array.isArray(assignedTo) ? assignedTo : []);
  }, [assignedTo]);

  const assigned = React.useMemo(
    () => localAssignedTo.length > 0,
    [localAssignedTo],
  );
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const { loggedInUser } = useLoggedInUser();

  const cliniciansIncludeMe = React.useMemo(() => {
    return (
      localAssignedTo &&
      localAssignedTo?.some(
        (clinician) => clinician.clinician_id === loggedInUser?.userId,
      )
    );
  }, [loggedInUser, localAssignedTo]);

  const canAssignPatients: boolean =
    selectedTenancy?.userPermissions?.[
      SystemPermission.ASSIGN_PATIENT_TO_OTHER
    ] || false;

  const shouldHidePlusButton = cliniciansIncludeMe && !canAssignPatients;

  const assignedToOptions = React.useMemo(() => {
    const options = [];
    if (!cliniciansIncludeMe) {
      options.push({
        id: 'assignToMe',
        value: 'assignToMe',
        label: 'Assign to me',
      });
    }
    if (canAssignPatients && hasClinicianOptions) {
      options.push({
        id: 'assignToSomeoneElse',
        value: 'assignToSomeoneElse',
        label: 'Assign to someone else',
      });
    }
    return options;
  }, [cliniciansIncludeMe, canAssignPatients, hasClinicianOptions]);
  const updateToastPosition = React.useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect?.();
    if (!rect) {
      setToastStyle(undefined);
      return;
    }

    const width = 200;
    const padding = 8;
    const desiredLeft = rect.left + rect.width / 2 - width / 2;
    const left = Math.min(
      Math.max(desiredLeft, padding),
      window.innerWidth - width - padding,
    );
    const top = rect.bottom + 8;

    setToastStyle({
      position: 'fixed',
      width,
      left,
      top,
      zIndex: 1029,
    });
  }, []);

  const onOpenAssignTo = (e: React.MouseEvent<SVGElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    anchorRef.current = e.currentTarget;
    updateToastPosition();
    setAssigningToOpen(true);
  };

  const onCloseAssignTo = (
    e?: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
  ) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setAssigningToOpen(false);
    anchorRef.current = null;
    setToastStyle(undefined);
  };

  const onSelectOption = (e: React.MouseEvent<HTMLElement>, value: string) => {
    e.stopPropagation();
    e.preventDefault();
    if (!loggedInUser) {
      return onCloseAssignTo();
    }
    void (async () => {
      try {
        await onSelectAssignTo(value, assignToClinician, loggedInUser.userId);
        if (value === 'assignToMe') {
          setLocalAssignedTo((prev) => {
            if (prev.some((c) => c.clinician_id === loggedInUser.userId)) {
              return prev;
            }
            return [
              ...prev,
              {
                id: `me-${loggedInUser.userId}`,
                clinician_id: loggedInUser.userId,
                first_name: loggedInUser.firstName,
                last_name: loggedInUser.lastName,
              },
            ];
          });
        }
      } finally {
        onCloseAssignTo();
      }
    })();
  };

  useEffect(() => {
    if (!assigningToOpen) return;

    const handleClickOutside = (event: any) => {
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }
      onCloseAssignTo();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [assigningToOpen]);

  useEffect(() => {
    if (!assigningToOpen) return;

    const handleViewportChange = () => {
      updateToastPosition();
    };

    updateToastPosition();
    document.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      document.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [assigningToOpen, updateToastPosition]);

  return (
    <>
      <div className="assignToColumn hide-in-pdf">
        {!inPatientDetails && assigned ? (
          <>
            <CheckCircleFill
              color="#B2CEE2"
              className="clickable"
              size={24}
              data-tip
              data-for={`assignedCompletedTip-${patientId}`}
              onClick={(e) => {
                onOpenAssignTo(e);
              }}
            />
            <ReactTooltip
              id={`assignedCompletedTip-${patientId}`}
              className="rounded-3"
              effect="solid"
              place={inPatientDetails ? 'bottom' : 'top'}
            >
              <div className="mb-1 d-flex flex-column gap-1">
                {localAssignedTo?.map((clinician) => (
                  <div key={clinician.id}>
                    {clinician.first_name} {clinician.last_name}
                  </div>
                ))}
              </div>
            </ReactTooltip>
          </>
        ) : (
          !shouldHidePlusButton && (
            <AddAssignToCircleButton
              onClick={(e) => onOpenAssignTo(e)}
              tooltipPlace={inPatientDetails ? 'bottom' : 'top'}
              recommended={recommended}
            />
          )
        )}
      </div>
      {assignedToOptions.length > 0 && (
        <Toast
          bg="light"
          ref={dropdownRef}
          show={assigningToOpen}
          onClose={onCloseAssignTo}
          className="assignToOptions"
          style={toastStyle}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {assignedToOptions.map((option, index) => (
            <div key={option.id}>
              <Toast.Body
                className="assignToOptionItem"
                onClick={(e) => onSelectOption(e, option.value)}
              >
                {option.label}
              </Toast.Body>
              {index !== assignedToOptions.length - 1 && (
                <hr className="my-0" />
              )}
            </div>
          ))}
        </Toast>
      )}
    </>
  );
};

export default ActionFormatterComponent;
